import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
} from "recharts";

import { GRAPH_COLORS } from "../../../types/micellinous-constants";

// to display text inside graph bars
const renderCustomBarLabel = ({ x, y, width, height, value, category }) => {
  const xPosValue = x + width / 2;
  const yPosValue = 340;
  const xPosCategory = x + 10;
  const yPosCategory = 215;
  const fontSize = "0.7em";

  return (
    value && (
      <>
        <text
          x={xPosValue}
          y={yPosValue}
          fill="#fff"
          fontSize={fontSize}
          textAnchor="middle"
          dominantBaseline="central"
          transform={`rotate(-90, ${xPosValue}, ${yPosValue})`}
          z={1000}
        >
          {`${value.toFixed(1)}%`}
        </text>
        <text
          x={xPosCategory}
          y={yPosCategory}
          fill="#fff"
          fontSize={fontSize}
          textAnchor="middle"
          dominantBaseline="central"
          transform={`rotate(-90, ${xPosCategory}, ${yPosCategory})`}
        >
          {category}
        </text>
      </>
    )
  );
};

const NarcoticsChart = ({ aggregatedData, timeUnits, timeFilter }) => {
  const [chartData, setChartData] = useState([]);
  const [keys, setKeys] = useState([]);

  // convert the provided aggregated data in a format which could be provided to rechart to display bars
  function formatChartData(inputData) {
    const result = {};

    for (const location in inputData) {
      const monthlyData = inputData[location];

      for (const month in monthlyData) {
        if (!result[month]) {
          result[month] = { name: month };
        }

        const qualitative = parseFloat(
          monthlyData[month].qualitative_compliance
        );
        const quantitative = parseFloat(
          monthlyData[month].quantitative_compliance
        );

        const qualitativeKey = `${location} - Qualitative`;
        const quantitativeKey = `${location} - Quantitative`;
        const qualitativeNonComplianceKey = `${qualitativeKey} Non-Compliance`;
        const quantitativeNonComplianceKey = `${quantitativeKey} Non-Compliance`;

        result[month][qualitativeKey] = qualitative;
        result[month][qualitativeNonComplianceKey] = (
          100 - qualitative
        ).toFixed(2);
        result[month][quantitativeKey] = quantitative;
        result[month][quantitativeNonComplianceKey] = (
          100 - quantitative
        ).toFixed(2);
      }
    }

    // Sort the result by the time in the name field
    if (timeFilter === "quarterly") {
      const sortedResult = Object.values(result).sort((a, b) => {
        const [aQuarter, aYear] = a.name.split("-");
        const [bQuarter, bYear] = b.name.split("-");

        const aQuarterNum = parseInt(aQuarter.substring(1), 10);
        const bQuarterNum = parseInt(bQuarter.substring(1), 10);

        if (aYear === bYear) {
          return aQuarterNum - bQuarterNum;
        } else {
          return aYear - bYear;
        }
      });

      return sortedResult;
    } else {
      const sortedResult = Object.values(result).sort((a, b) => {
        const [aMonth, aYear] = a.name.split("-").map(Number);
        const [bMonth, bYear] = b.name.split("-").map(Number);

        if (aYear === bYear) {
          return aMonth - bMonth;
        } else {
          return aYear - bYear;
        }
      });

      return sortedResult;
    }
  }

  useEffect(() => {
    setChartData(formatChartData(aggregatedData));
  }, [aggregatedData]);

  // find json key in the chart data to be used for parsing the data
  const findKeys = () => {
    const allKeys = new Set();

    chartData.forEach((entry) => {
      Object.keys(entry).forEach((key) => {
        if (key !== "name" && !key.includes("Non-Compliance")) {
          allKeys.add(key);
        }
      });
    });

    setKeys(Array.from(allKeys));
  };

  useEffect(() => {
    findKeys();
  }, [chartData]);

  // useEffect(() => {
  //   console.log("Chart Data:\n ", chartData);
  // }, [chartData]);

  // useEffect(() => {
  //   console.log("Keys: \n", keys);
  // }, [chartData]);

  return (
    <div>
      <div style={{ textAlign: "center" }}>
        <span
          style={{
            color: GRAPH_COLORS[0],
            marginRight: "5px",
            marginLeft: "10px",
            display: "inline-block",
            width: "10px",
            height: "10px",
            backgroundColor: GRAPH_COLORS[0],
            borderRadius: "50%",
          }}
        ></span>
        <span>Compliant</span>
        <span
          style={{
            color: GRAPH_COLORS[1],
            marginRight: "5px",
            marginLeft: "10px",
            display: "inline-block",
            width: "10px",
            height: "10px",
            backgroundColor: GRAPH_COLORS[1],
            borderRadius: "50%",
          }}
        ></span>
        <span>Non-Compliant</span>
      </div>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
          barCategoryGap={20}
          barSize={25}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis
            label={{ value: "Compliance", angle: -90, position: "insideLeft" }}
            domain={[0, 100]}
          />
          {/* <Tooltip /> */}
          {keys.map((key, index) => (
            <Bar key={key} dataKey={key} stackId={index} fill={GRAPH_COLORS[0]}>
              <LabelList
                dataKey={key}
                content={(props) =>
                  renderCustomBarLabel({ ...props, category: key })
                }
              />
            </Bar>
          ))}
          {keys.map((key, index) => (
            <Bar
              key={`${key} Non-Compliance`}
              dataKey={`${key} Non-Compliance`}
              stackId={index}
              fill={GRAPH_COLORS[1]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default NarcoticsChart;
